import React, { useState, useEffect } from 'react';
import fullLogo from '@/assets/ingesta-full-logo.png';
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderOpen,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
  Settings,
  ArrowLeftRight,
  MoreHorizontal,
  FileText,
  Shield,
  Eye,
  Layers,
  FlaskConical,
  Users,
  Loader2,
  Pencil,
  Circle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/store/auth';
import { useWorkspaceStore } from '@/store/workspace';
import { getWorkspaceProjects, getUserDraftsInWorkspace } from '@/lib/database';
import { useProjectsRefresh } from '@/store/projectsRefresh';
import { TrialBanner } from '@/components/subscription/TrialBanner';
import { ActionBarProvider, useActionBarValue } from '@/contexts/ActionBarContext';

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  path?: string;
  isActive?: boolean;
  onClick: () => void;
  rightAdornment?: React.ReactNode;
}

function SidebarItem({ icon, label, isActive, onClick, rightAdornment }: SidebarItemProps) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
        isActive
          ? 'bg-ingesta-blue-600 text-white'
          : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      {icon}
      <span className="flex-1 text-left">{label}</span>
      {rightAdornment}
    </button>
  );
}

interface SidebarProject {
  id: string;
  name: string;
  overall_health: 'green' | 'yellow' | 'red' | null;
  // A draft is a *condition*, not a health status — it's the current user's
  // unsaved project. Drives a distinct affordance (pencil icon, italic
  // title, "Draft" tag) rather than a health tint.
  isDraft: boolean;
}

// Status → icon tint. Anything missing (null / unknown) falls back to a
// neutral gray so the project still reads as a project, not as healthy.
function projectIconColor(health: SidebarProject['overall_health']): string {
  switch (health) {
    case 'green': return 'text-green-500';
    case 'yellow': return 'text-yellow-500';
    case 'red': return 'text-red-500';
    default: return 'text-gray-400';
  }
}

export function DashboardLayout() {
  return (
    <ActionBarProvider>
      <DashboardLayoutInner />
    </ActionBarProvider>
  );
}

function ActionBarSlot() {
  const actionBar = useActionBarValue();
  if (!actionBar) return null;
  return (
    <div className="border-t border-gray-200 bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.05)] flex-shrink-0">
      {actionBar}
    </div>
  );
}

function DashboardLayoutInner() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarProjects, setSidebarProjects] = useState<SidebarProject[]>([]);
  // Tracks whether we've ever finished a sidebar-projects fetch. The full
  // "Loading…" placeholder is only shown while this is false — i.e. on the
  // very first cold fetch. Subsequent refreshes (rename, set-active,
  // navigation) keep the existing list visible.
  const [hasFetchedSidebarProjects, setHasFetchedSidebarProjects] = useState(false);
  // Tracks whether a refresh is currently in flight. Drives a small inline
  // spinner next to the "Projects" sidebar item so the user knows something
  // is happening without the list flashing or clearing.
  const [isRefreshingSidebarProjects, setIsRefreshingSidebarProjects] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, isSuperadmin } = useAuthStore();
  const { currentWorkspace, assumedRole, assumedWorkspaceId, clearAssumedRole, canEdit, selectWorkspace } = useWorkspaceStore();
  const projectsVersion = useProjectsRefresh(s => s.version);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link from invite emails: `?workspace=<id>` switches to that workspace
  // (e.g. the one the user was just added to) then strips the param so it
  // doesn't re-fire on later navigation. No-op if already on that workspace.
  useEffect(() => {
    const target = searchParams.get('workspace');
    if (!target) return;
    if (currentWorkspace?.id !== target) {
      selectWorkspace(target).catch((err) => {
        console.error('Failed to open workspace from invite link:', err);
      });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('workspace');
    setSearchParams(next, { replace: true });
  }, [searchParams, currentWorkspace?.id, selectWorkspace, setSearchParams]);

  // Fetch projects for sidebar when workspace changes, on navigation, or
  // when something the user did (rename, promote draft, etc.) bumped the
  // projects version. The list is NOT cleared during refetch — we keep
  // the existing data on screen and just toggle a small inline spinner.
  useEffect(() => {
    const loadProjects = async () => {
      if (!currentWorkspace) {
        setSidebarProjects([]);
        setHasFetchedSidebarProjects(true);
        return;
      }
      setIsRefreshingSidebarProjects(true);
      try {
        // Active projects exclude drafts; drafts are fetched separately and
        // are creator-scoped (RLS + an explicit created_by filter), so a user
        // only ever sees their own unsaved drafts. Drafts sort to the top
        // (newest first) since they're what the user is actively working on.
        const [projects, drafts] = await Promise.all([
          getWorkspaceProjects(currentWorkspace.id),
          getUserDraftsInWorkspace(currentWorkspace.id),
        ]);
        setSidebarProjects([
          ...drafts.map(p => ({
            id: p.id,
            name: p.name,
            overall_health: null,
            isDraft: true,
          })),
          ...projects.map(p => ({
            id: p.id,
            name: p.name,
            overall_health: (p.overall_health as SidebarProject['overall_health']) ?? null,
            isDraft: false,
          })),
        ]);
      } catch (error) {
        console.error('Failed to load sidebar projects:', error);
      } finally {
        setIsRefreshingSidebarProjects(false);
        setHasFetchedSidebarProjects(true);
      }
    };
    loadProjects();
  }, [currentWorkspace, location.pathname, projectsVersion]);

  const handleLogout = () => {
    logout();
    // Note: logout() handles redirect to marketing site
  };


  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        w-64 bg-white border-r flex flex-col flex-shrink-0
        fixed top-0 left-0 h-screen z-50 transform transition-transform
        lg:relative lg:h-screen lg:translate-x-0 lg:z-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Header Section */}
        <div className="px-4 border-b lg:border-b-0">
          <div className="flex items-center justify-between lg:justify-start">
            <img src={fullLogo} alt="Ingesta" className="h-16" />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Workspace Selector */}
        {currentWorkspace && (
          <div className="px-4 pb-3 border-b">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-between px-3 h-auto py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Layers className="h-4 w-4 text-gray-500 shrink-0" />
                    <span className="text-sm font-medium truncate">{currentWorkspace.name}</span>
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-500 shrink-0 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/workspace/select')} className="justify-between">
                  <div className="flex items-center">
                    <ArrowLeftRight className="h-4 w-4 mr-2" />
                    <span>Switch workspace</span>
                  </div>
                  <MoreHorizontal className="h-4 w-4 text-gray-400" />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Content Section - Flexible */}
        <div className="flex-1 flex flex-col p-4 overflow-hidden">
          {/* Navigation */}
          <nav className="space-y-1 flex-1 overflow-y-auto">
            {/* Home */}
            <SidebarItem
              icon={<LayoutDashboard className="h-5 w-5" />}
              label="Home"
              path="/home"
              isActive={location.pathname === '/home'}
              onClick={() => {
                navigate('/home');
                setSidebarOpen(false);
              }}
            />

            {/* Documents */}
            <SidebarItem
              icon={<FileText className="h-5 w-5" />}
              label="Documents"
              path="/documents"
              isActive={location.pathname === '/documents'}
              onClick={() => {
                navigate('/documents');
                setSidebarOpen(false);
              }}
            />

            {/* Projects Section */}
            <div>
              <SidebarItem
                icon={<FolderOpen className="h-5 w-5" />}
                label="Projects"
                path="/projects"
                isActive={location.pathname === '/projects'}
                onClick={() => {
                  navigate('/projects');
                  setSidebarOpen(false);
                }}
                rightAdornment={
                  isRefreshingSidebarProjects ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                  ) : null
                }
              />

              <div className="ml-4 mt-1 space-y-1">
                {!hasFetchedSidebarProjects ? (
                  <div className="px-3 py-1.5 flex items-center gap-2 text-sm text-gray-400">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Loading…</span>
                  </div>
                ) : sidebarProjects.length === 0 ? (
                  <p className="px-3 py-1.5 text-sm text-gray-400 italic">No projects yet</p>
                ) : (
                  sidebarProjects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => {
                        navigate(`/projects/${project.id}`);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                        location.pathname === `/projects/${project.id}`
                          ? 'bg-ingesta-blue-100 text-ingesta-blue-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {project.isDraft ? (
                        <Pencil className="h-4 w-4 flex-shrink-0 text-gray-400" />
                      ) : (
                        // Filled status dot — the health color is the whole
                        // message. Wrapped in a 4x4 box so it lines up with the
                        // draft pencil and keeps the row's left edge consistent.
                        <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                          <Circle className={`h-2.5 w-2.5 fill-current ${projectIconColor(project.overall_health)}`} />
                        </span>
                      )}
                      <span className={`truncate min-w-0 ${project.isDraft ? 'italic text-gray-500' : ''}`}>
                        {project.name || '(Untitled)'}
                      </span>
                      {project.isDraft && (
                        <span className="ml-auto flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                          Draft
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Members */}
            <SidebarItem
              icon={<Users className="h-5 w-5" />}
              label="Members"
              path="/members"
              isActive={location.pathname === '/members'}
              onClick={() => {
                navigate('/members');
                setSidebarOpen(false);
              }}
            />

            {/* Settings — admins only. Lives in the sidebar (not the
                workspace dropdown) so it's discoverable as a top-level
                destination instead of buried behind the workspace name. */}
            {canEdit() && (
              <SidebarItem
                icon={<Settings className="h-5 w-5" />}
                label="Settings"
                path="/workspace/settings"
                isActive={location.pathname === '/workspace/settings'}
                onClick={() => {
                  navigate('/workspace/settings');
                  setSidebarOpen(false);
                }}
              />
            )}

            {/* Admin/Dev Section - only for superadmins or test users */}
            {(isSuperadmin || user?.email?.match(/^test-.*@test\.ingesta\.dev$/)) && (
              <>
                <div className="my-4 border-t border-gray-200" />

                {isSuperadmin && (
                  <SidebarItem
                    icon={<Shield className="h-5 w-5" />}
                    label="Admin Panel"
                    path="/admin/super-panel"
                    isActive={location.pathname === '/admin/super-panel'}
                    onClick={() => {
                      navigate('/admin/super-panel');
                      setSidebarOpen(false);
                    }}
                  />
                )}

                <SidebarItem
                  icon={<FlaskConical className="h-5 w-5" />}
                  label="Testing"
                  path="/dev/testing"
                  isActive={location.pathname === '/dev/testing'}
                  onClick={() => {
                    navigate('/dev/testing');
                    setSidebarOpen(false);
                  }}
                />
              </>
            )}
          </nav>
        </div>

      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top navigation */}
        <header className="bg-white border-b px-4 py-0 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </Button>

              {/* Mobile logo - shown when sidebar is hidden */}
              <img src={fullLogo} alt="Ingesta" className="h-16 lg:hidden" />
            </div>

            <div className="flex items-center space-x-3 py-3">
              {/* Notifications */}
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5" />
              </Button>

              {/* User dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className={`flex items-center gap-2 px-2 ${isSuperadmin ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isSuperadmin ? 'bg-amber-600' : 'bg-ingesta-blue-600'}`}>
                      <span className="text-white text-sm font-medium">
                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                    <ChevronDown className={`h-4 w-4 ${isSuperadmin ? 'text-white' : 'text-gray-500'}`} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/user/settings')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Trial Banner */}
        <TrialBanner />

        {/* Assumed Role Banner */}
        {assumedRole && assumedWorkspaceId === currentWorkspace?.id && (
          <div className={`px-4 py-2 flex items-center justify-between ${
            assumedRole === 'admin'
              ? 'bg-purple-100 border-b border-purple-200'
              : 'bg-blue-100 border-b border-blue-200'
          }`}>
            <div className="flex items-center gap-2">
              {assumedRole === 'admin' ? (
                <Shield className="h-4 w-4 text-purple-600" />
              ) : (
                <Eye className="h-4 w-4 text-blue-600" />
              )}
              <span className={`text-sm font-medium ${
                assumedRole === 'admin' ? 'text-purple-800' : 'text-blue-800'
              }`}>
                Viewing as {assumedRole === 'admin' ? 'Admin' : 'Reader'}
              </span>
              <span className="text-sm text-gray-600">
                (Superadmin access)
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                clearAssumedRole();
                navigate('/workspace/select');
              }}
              className={`h-7 text-xs ${
                assumedRole === 'admin'
                  ? 'text-purple-700 hover:text-purple-900 hover:bg-purple-200'
                  : 'text-blue-700 hover:text-blue-900 hover:bg-blue-200'
              }`}
            >
              Exit assumed role
            </Button>
          </div>
        )}

        {/* Page content */}
        <main className="p-6 flex-1 overflow-y-auto">
          <Outlet />
        </main>

        {/* Layout-level action bar slot (sibling of main, outside its scroll/padding) */}
        <ActionBarSlot />
      </div>
    </div>
  );
}